import React, { useMemo, useRef, useState } from "react";
import { Field } from "react-final-form";
import {
  Box,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  TextField,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseRounded from "@mui/icons-material/CloseRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import isEmpty from "lodash/isEmpty";
import Txt from "../../typography/Txt";
import colors from "../../../theme/colors";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * RffFileUploadField — drag-and-drop / click-to-browse file picker.
 *
 * Every instance on a form writes into the SAME `uploadMetadata` array, and each
 * entry carries the `docGroup` / `docType` of the field that added it. That is
 * what `useAxiosHook` reads when it builds the multipart body, so a form can
 * have several upload fields (photo ID, supporting documents) and still submit
 * one flat file list the backend can route by category.
 *
 * Each field only ever displays and validates the entries matching its own
 * group/type pair.
 */
const RffFileUploadField = ({
  docGroup,
  docType,
  label,
  required = false,
  helperText,
  suppressGrid,
  size,
  height = "180px",
  uploadText = "Drag & drop files here, or click to browse",
  suppressIcon,
  suppressButton,
  allowRename = false,
  sx,
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);
  // A counter, not a boolean: dragging over a child element fires dragleave on
  // the parent, which would otherwise clear the highlight mid-drag.
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const matchesThisField = useMemo(
    () => (entry) => {
      const matchesGroup = docGroup ? entry.docGroup === docGroup : !entry.docGroup;
      const matchesType = docType ? entry.docType === docType : !entry.docType;
      return matchesGroup && matchesType;
    },
    [docGroup, docType],
  );

  const validate = useMemo(() => {
    if (!required) return undefined;
    return (value) => {
      const matching = Array.isArray(value) ? value.filter(matchesThisField) : [];
      return matching.length === 0 ? "At least one file is required" : undefined;
    };
  }, [required, matchesThisField]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDraggingOver(false);
  };

  const ensureFileExtension = (newName, originalName) => {
    const extension = originalName.split(".").pop().toLowerCase();
    return `${newName.replace(/\.[^/.]+$/, "")}.${extension}`;
  };

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
    >
      <Field
        name="uploadMetadata"
        validate={validate}
        subscription={{ value: true, error: true, touched: true }}
        render={({ input, meta }) => {
          const allFiles = Array.isArray(input.value) ? input.value : [];
          const displayedFiles = allFiles.filter(matchesThisField);

          const onFilesAdded = (rawFiles) => {
            const added = rawFiles.map((rawFile) => ({
              id: newUploadId(),
              file: rawFile,
              name: rawFile.name,
              file_display_name: rawFile.name,
              docGroup: docGroup || null,
              docType: docType || null,
            }));
            input.onChange([...allFiles, ...added]);
          };

          const onFileDelete = (indexInDisplayed) => {
            const target = displayedFiles[indexInDisplayed];
            input.onChange(allFiles.filter((f) => f !== target));
          };

          const onFileRename = (fileId, newName) => {
            input.onChange(
              allFiles.map((f) =>
                f.id === fileId
                  ? {
                      ...f,
                      file_display_name: ensureFileExtension(newName, f.name),
                    }
                  : f,
              ),
            );
          };

          const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setIsDraggingOver(false);
            if (!e.dataTransfer.files) return;
            onFilesAdded(Array.from(e.dataTransfer.files));
          };

          const saveEdit = () => {
            if (editingFileId && editingValue.trim()) {
              onFileRename(editingFileId, editingValue);
            }
            setEditingFileId(null);
            setEditingValue("");
          };

          const cancelEdit = () => {
            setEditingFileId(null);
            setEditingValue("");
          };

          return (
            <>
              <Box
                onDragEnter={handleDragEnter}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 1.5,
                  height,
                  p: 2,
                  cursor: "pointer",
                  borderRadius: 1,
                  transition: "all 100ms ease-in-out",
                  background: isDraggingOver
                    ? alpha(theme.palette.primary.main, 0.06)
                    : "transparent",
                  ...(sx || {}),
                  border: `2px dashed ${
                    isDraggingOver ? theme.palette.primary.main : colors.slate300
                  }`,
                  color: isDraggingOver
                    ? theme.palette.primary.main
                    : colors.slate600,
                  // Sits flush on top of the file list below it when there is one.
                  borderBottomLeftRadius: isEmpty(displayedFiles) ? undefined : 0,
                  borderBottomRightRadius: isEmpty(displayedFiles) ? undefined : 0,
                }}
              >
                {suppressIcon ? null : (
                  <FileUploadOutlined
                    sx={{ opacity: isDraggingOver ? 0.9 : 0.6, color: "inherit" }}
                  />
                )}
                <Txt sx={{ fontSize: 13, color: "inherit" }}>{uploadText}</Txt>
                {suppressButton ? null : (
                  <Txt
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.primary.main}`,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Browse Files
                  </Txt>
                )}
              </Box>

              {/* Deliberately a SIBLING of the dropzone, not a child: clicking
                  the dropzone calls input.click(), and a programmatic click
                  bubbles — nested, it would re-enter the dropzone's own onClick
                  and recurse until the stack blew. */}
              <Box
                component="input"
                ref={inputRef}
                type="file"
                multiple
                onChange={(e) => {
                  if (!e.target.files) return;
                  onFilesAdded(Array.from(e.target.files));
                  // Reset so re-picking the same file still fires a change.
                  e.target.value = "";
                }}
                sx={{ display: "none" }}
              />

              {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
              )}

              {displayedFiles.length > 0 && (
                <List
                  sx={{
                    p: 0,
                    overflow: "hidden",
                    border: `1px solid ${colors.slate300}`,
                    borderTop: 0,
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                  }}
                >
                  {displayedFiles.map((fileObj, idx) => (
                    <ListItem
                      key={fileObj.id}
                      sx={{
                        p: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: colors.slate50,
                        borderBottom:
                          idx + 1 === displayedFiles.length
                            ? "none"
                            : `1px solid ${colors.slate200}`,
                      }}
                    >
                      {editingFileId === fileObj.id ? (
                        <>
                          <TextField
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                            onBlur={saveEdit}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            size="small"
                            sx={{
                              flex: 1,
                              m: 0.5,
                              "& .MuiInputBase-input": {
                                fontSize: 12,
                                py: 0.5,
                                px: 1,
                              },
                            }}
                          />
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelEdit();
                            }}
                          >
                            <CloseRounded sx={{ fontSize: 14 }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Txt
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              p: 1,
                              fontSize: 12,
                              wordBreak: "break-all",
                              color: colors.slate700,
                              cursor: allowRename ? "text" : "default",
                              "&:hover": allowRename
                                ? {
                                    textDecoration: "underline",
                                    background: colors.slate100,
                                  }
                                : undefined,
                            }}
                            onClick={(e) => {
                              if (!allowRename) return;
                              e.stopPropagation();
                              setEditingFileId(fileObj.id);
                              setEditingValue(fileObj.file_display_name);
                            }}
                          >
                            {fileObj.file_display_name}
                          </Txt>
                          <IconButton
                            color="error"
                            size="small"
                            aria-label={`Remove ${fileObj.file_display_name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFileDelete(idx);
                            }}
                          >
                            <CloseRounded sx={{ fontSize: 14 }} />
                          </IconButton>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          );
        }}
      />
    </RffInputWrapper>
  );
};

/**
 * Ids only need to be unique within one form session — they key the React list
 * and name the multipart part. `crypto.randomUUID` isn't available on insecure
 * origins, hence the fallback.
 */
let uploadIdCounter = 0;
const newUploadId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  uploadIdCounter += 1;
  return `upload-${uploadIdCounter}-${Math.random().toString(36).slice(2, 10)}`;
};

export default RffFileUploadField;
